/**
 * Varied synth composer (study v2). Same request and palette as v1
 * (compose.ts), rebuilt around four techniques that keep a piece alive:
 *
 * 1. ENERGY ARC. Every section has an energy (0-1) that ramps into the next.
 *    It picks the layers, drum density, arp register, velocities and filter
 *    brightness per note, so builds sweep open and breakdowns close down.
 * 2. MOTIF DEVELOPMENT. One seeded motif grows into question/answer phrases
 *    through sequence, inversion, retrograde, displacement and fragmentation;
 *    the chorus gets its own motif, later verses repeat with ~30% variation, a
 *    counter-melody answers in the lead's gaps, the last chorus doubles up.
 * 3. SECTION HARMONY. Verse, chorus and bridge have different chord loops and
 *    harmonic rhythm, with sus/add9 colour, a borrowed chord, a tonic pedal in
 *    breakdowns and an optional key lift for the final chorus.
 * 4. GENERATED RHYTHM. Euclidean drum/percussion patterns, odd-length arps
 *    that phase against the bar, phrase-end fills, a dropped beat and a crash
 *    into each chorus, and echo throws on phrase endings.
 */
import {
  ARCHETYPES, KEYS, MODES, NOUNS, PALETTES, PROGRESSIONS, Rng, Scale,
  type InstId, type SNote, type Section, type SynthRequest, type SynthScore, type FxParams,
} from './compose';
import { makeV2Patches } from './instruments2';

export const SYNTH_COMPOSER_VERSION_2 = 'synth-study-2';
type Kind = 'intro' | 'verse' | 'pre' | 'chorus' | 'break' | 'bridge' | 'outro';
interface Plan { label: string; kind: Kind; bars: number; energy: number }
interface Chord { root: number; tones: number[] } // pitch classes relative to the song key

/** Evenly spread k hits over n steps (Bjorklund/Toussaint), rotated. */
export function euclid(k: number, n: number, rotate = 0): boolean[] {
  const out: boolean[] = [];
  for (let i = 0; i < n; i++) out.push(((i * k) % n) < k);
  return out.map((_, i) => out[(i + rotate + n) % n]);
}
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (x: number, a = 0, b = 1) => Math.max(a, Math.min(b, x));

function planForm(scene: SynthRequest['scene'], r: Rng): Plan[] {
  const len = (a: number, b: number) => r.chance(.5) ? a : b;
  if (scene === 'exploration') {
    // Slow waves of energy rather than a pop form.
    const peaks = [.12, r.range(.3, .4), r.range(.45, .6), r.range(.3, .4), r.range(.6, .72), .18];
    const names = ['horizon', 'signals', 'song', 'drift', 'swell', 'return'];
    const kinds: Kind[] = ['intro', 'verse', 'chorus', 'break', 'chorus', 'outro'];
    return names.map((label, i) => ({ label, kind: kinds[i], bars: 8, energy: peaks[i] }));
  }
  if (scene === 'small-city') {
    return [
      { label: 'intro', kind: 'intro', bars: 4, energy: .2 },
      { label: 'verse', kind: 'verse', bars: 8, energy: .42 },
      { label: 'hook', kind: 'chorus', bars: 8, energy: .66 },
      { label: 'verse II', kind: 'verse', bars: 8, energy: .5 },
      { label: 'bridge', kind: 'bridge', bars: len(4, 8), energy: .34 },
      { label: 'hook II', kind: 'chorus', bars: 8, energy: .8 },
      { label: 'outro', kind: 'outro', bars: 4, energy: .22 },
    ];
  }
  if (scene === 'fight') return [
    { label: 'standoff', kind: 'intro', bars: 4, energy: .5 },
    { label: 'clash', kind: 'verse', bars: 8, energy: .78 },
    { label: 'charge', kind: 'pre', bars: 4, energy: .88 },
    { label: 'onslaught', kind: 'chorus', bars: 8, energy: 1 },
    { label: 'breath', kind: 'break', bars: 4, energy: .45 },
    { label: 'clash II', kind: 'verse', bars: 8, energy: .85 },
    { label: 'last stand', kind: 'chorus', bars: 8, energy: 1 },
    { label: 'aftermath', kind: 'outro', bars: 4, energy: .35 },
  ];
  const drive = scene === 'flight';
  const plan: Plan[] = [
    { label: drive ? 'launch' : 'intro', kind: 'intro', bars: 4, energy: .22 },
    { label: drive ? 'cruise' : 'verse', kind: 'verse', bars: 8, energy: .46 },
    { label: 'build', kind: 'pre', bars: 4, energy: .64 },
    { label: drive ? 'theme' : 'chorus', kind: 'chorus', bars: 8, energy: .86 },
    { label: drive ? 'nebula' : 'breakdown', kind: 'break', bars: len(4, 8), energy: .28 },
    { label: drive ? 'cruise II' : 'verse II', kind: 'verse', bars: 8, energy: .56 },
    { label: 'build II', kind: 'pre', bars: 4, energy: .74 },
    { label: drive ? 'theme II' : 'chorus II', kind: 'chorus', bars: 8, energy: 1 },
    { label: drive ? 'arrival' : 'outro', kind: 'outro', bars: 4, energy: .24 },
  ];
  // Some songs skip the first build and go straight into the chorus.
  if (r.chance(.35)) plan.splice(2, 1);
  return plan;
}

export function composeSynth2(req: SynthRequest): SynthScore {
  const stream = (layer: string) => new Rng(req.seed, `v2|${req.biome}|${req.scene}|${req.night ? 'n' : 'd'}|${layer}`);
  const world = new Rng(req.seed, 'world');
  const a = ARCHETYPES[req.scene], p = PALETTES[req.biome];
  const key = world.int(0, 11);
  // Reference-led: city/flight tracks on warmer worlds often use harmonic minor
  // (the raised 7th's major V gives the dark, driving pull of the reference).
  const modeRng = new Rng(req.seed, `mode2|${req.biome}`);
  const pulseScene = req.scene === 'large-city' || req.scene === 'flight' || req.scene === 'fight';
  // Draw both choices in a fixed order so every scene on a planet shares its base mode.
  const harmonic = modeRng.chance(.5), baseMode = modeRng.pick(p.modes);
  const mode = pulseScene && !['ice', 'ocean'].includes(req.biome) && harmonic ? 'harmonic minor' : baseMode;
  const steps = MODES[mode], scale = new Scale(key, steps);
  const form = stream('form');
  const tempo: [number, number] = req.scene === 'large-city' ? [112, 124] : a.tempo;
  const bpm = Math.round(form.range(...tempo) * (req.night ? .88 : 1));
  const plan = planForm(req.scene, form);
  const ambient = req.scene === 'exploration', chill = req.scene === 'small-city', drive = req.scene === 'flight' || req.scene === 'fight';
  const patches = makeV2Patches(req);
  const words = stream('title');
  const title = `${words.pick(p.words)} ${words.pick(NOUNS[req.scene])}`;

  // ── harmony per section ───────────────────────────────────────────────────
  const hr = stream('harmony');
  const progs = PROGRESSIONS[mode] ?? PROGRESSIONS.aeolian;
  const verseProg = hr.pick(progs);
  let chorusProg = hr.pick(progs); for (let i = 0; i < 4 && chorusProg === verseProg; i++) chorusProg = hr.pick(progs);
  if (chorusProg === verseProg) chorusProg = [verseProg[2], verseProg[3], verseProg[0], verseProg[1]];
  const colour = ambient || chill ? hr.pick(['add9', 'sus2', '7'] as const) : hr.pick(['triad', 'add9', 'sus4'] as const);
  const minorish = steps[2] === 3;
  // Borrowed chord from the parallel scale: ♭VI/♭VII into a major-ish tonic, IV major into a minor one.
  const borrowed: Chord = minorish ? { root: 5, tones: [5, 9, 12] } : { root: 8, tones: [8, 12, 15] };
  const useBorrowed = hr.chance(.6);
  const lift = (req.scene === 'large-city' || drive) && hr.chance(.5) ? 2 : 0;
  const diatonic = (degree: number, kind: 'triad' | 'add9' | 'sus2' | 'sus4' | '7'): Chord => {
    const pc = (d: number) => steps[((d % 7) + 7) % 7] + 12 * Math.floor(d / 7);
    const root = pc(degree), third = pc(degree + 2), fifth = pc(degree + 4);
    const tones = kind === 'sus2' ? [root, pc(degree + 1), fifth] : kind === 'sus4' ? [root, pc(degree + 3), fifth]
      : kind === 'add9' ? [root, third, fifth, pc(degree + 8)] : kind === '7' ? [root, third, fifth, pc(degree + 6)] : [root, third, fifth];
    return { root, tones };
  };
  const chordName = (c: Chord) => KEYS[(key + c.root) % 12];

  // ── motif ─────────────────────────────────────────────────────────────────
  const mr = stream('motif');
  const RHYTHMS = [[0, 1, 1.5, 2.5, 3], [0, .5, 1, 2, 3], [0, 1.5, 2, 3], [.5, 1, 2, 2.5, 3.5], [0, .75, 1.5, 2, 3], [0, 1, 2, 2.75, 3.25]];
  const makeMotif = (r: Rng) => {
    const rhythm = r.pick(RHYTHMS); let d = r.pick([0, 2, 4]);
    return rhythm.map(t => { const n = { t, d }; d += r.pick([-2, -1, -1, 1, 1, 2, 3, -3]); d = clamp(d, -3, 9); return n; });
  };
  const verseMotif = makeMotif(mr), chorusMotif = makeMotif(stream('motif-chorus'));
  type M = { t: number; d: number }[];
  const TRANSFORMS: Record<string, (m: M, r: Rng) => M> = {
    repeat: m => m,
    sequence: (m, r) => { const s = r.pick([1, 2, -1]); return m.map(n => ({ ...n, d: n.d + s })); },
    inversion: m => m.map(n => ({ ...n, d: 2 * m[0].d - n.d })),
    retrograde: m => m.map((n, i) => ({ t: n.t, d: m[m.length - 1 - i].d })),
    displace: m => m.map(n => ({ ...n, t: Math.min(3.75, n.t + .5) })),
    fragment: m => { const f = m.slice(0, Math.max(2, Math.ceil(m.length / 2))); return [...f, ...f.map(n => ({ t: n.t + 2, d: n.d + 1 }))].filter(n => n.t < 4); },
  };

  // ── rhythm material ───────────────────────────────────────────────────────
  const rr = stream('rhythm');
  const arpLen = rr.pick(ambient ? [5, 6, 7] : [3, 5, 6, 7, 8]);
  // Rapid DESCENDING runs: each run falls through the chord from the top; odd
  // run lengths phase against the bar, and every other run starts a step lower.
  const arpShape = Array.from({ length: arpLen }, (_, i) => i);
  const percK = rr.pick([3, 5, 7]), percRot = rr.int(0, 7);
  const fillKind = rr.pick(['snare-roll', 'toms', 'stutter'] as const);

  const notes: SNote[] = []; const sections: Section[] = []; const energy: number[] = []; const automation: { beat: number; tone: number }[] = [];
  const hum = stream('human'), drums = stream('drums'), counter = stream('counter'), stabR = stream('stab'), breakR = stream('breaks');
  const breaks: { beat: number; beats: number }[] = [];
  let voicing: number[] = [], bar = 0, verseCount = 0, chorusCount = 0, prevLead = 0;
  const leadMemory = new Map<string, SNote[]>(); // re-used phrases for repeat-with-variation
  for (let si = 0; si < plan.length; si++) {
    const s = plan[si], next = plan[si + 1], isLastChorus = s.kind === 'chorus' && !plan.slice(si + 1).some(x => x.kind === 'chorus');
    if (s.kind === 'verse') verseCount++; if (s.kind === 'chorus') chorusCount++;
    const shift = isLastChorus ? lift : 0;
    const prog = s.kind === 'chorus' ? chorusProg : verseProg;
    const harmonicBars = s.kind === 'chorus' || drive ? 1 : 2;
    const layers = new Set<InstId>();
    for (let b = 0; b < s.bars; b++, bar++) {
      const t0 = bar * 4;
      // Energy ramps into the next section over the last bars (builds rise, outros fall).
      const ramp = s.kind === 'pre' ? b / s.bars : clamp((b - (s.bars - 2)) / 2);
      const e = clamp(lerp(s.energy, next?.energy ?? s.energy * .5, s.kind === 'pre' ? ramp : ramp * .35));
      energy.push(e);
      const bright = clamp(.45 + .75 * e, .35, 1.25) * (s.kind === 'pre' ? lerp(.55, 1.15, b / s.bars) : 1);
      const vel = .55 + .5 * e;
      // Chord for this bar.
      const pedal = s.kind === 'break' || (ambient && s.kind === 'intro');
      let chord = diatonic(prog[Math.floor(b / harmonicBars) % prog.length], colour);
      if (useBorrowed && s.kind === 'chorus' && b % 4 === 3 && b > 0) chord = borrowed;
      if (pedal && b % 2 === 1) chord = diatonic(prog[1] ?? 0, 'sus2');
      const pc = (x: number) => key + shift + x;
      const barPhrase = b % 4; // 4-bar phrases
      const phraseEnd = barPhrase === 3, sectionEnd = b === s.bars - 1, intoChorus = sectionEnd && next?.kind === 'chorus';

      // ── pads / drone / keys ─────────────────────────────────────────────
      if (b % harmonicBars === 0 || pedal) {
        const target = voicing.length ? voicing : [60, 64, 67, 71];
        voicing = chord.tones.map((x, i) => { let best = pc(x) + 60; for (let o = -36; o <= 36; o += 12) if (Math.abs(pc(x) + 48 + o - (target[i] ?? 62)) < Math.abs(best - (target[i] ?? 62))) best = pc(x) + 48 + o; return clamp(best, 52, 77); }).sort((u, v) => u - v);
        const len = pedal ? 4 : 4 * harmonicBars;
        const stabbed = pulseScene && (s.kind === 'verse' || s.kind === 'pre' || s.kind === 'chorus');
        if (!stabbed) { for (const pitch of voicing) notes.push({ inst: 'pad', pitch, beat: t0, beats: len, vel: .4 + .42 * e, bright: bright * .8, brightEnd: bright }); layers.add('pad'); }
        else {
          // Chopped chords: 16th pulses while building, staccato quarter pumps once it drives.
          for (let hb = 0; hb < harmonicBars; hb++) {
            const quarter = e >= .6, step = quarter ? 1 : .25;
            for (let x = 0; x < 4; x += step) {
              if (!quarter && (x % 1 === .75) && stabR.chance(.4)) continue;
              for (const pitch of voicing) notes.push({ inst: 'stab', pitch, beat: t0 + hb * 4 + x, beats: quarter ? .45 : .18, vel: (quarter ? .85 : (x % 1 === 0 ? .8 : .55)) * vel, bright });
            }
          }
          layers.add('stab');
        }
      }
      if ((ambient || s.kind === 'break' || s.kind === 'intro') && b % 8 === 0) {
        for (const pitch of [pc(0) + 36, pc(7) + 36]) notes.push({ inst: 'drone', pitch, beat: t0, beats: 32, vel: .8 });
        layers.add('drone');
      }
      if (chill && s.kind !== 'bridge' && e > .15) {
        const stabs = req.night ? [0] : e > .6 ? [0, 1.5, 2.5, 3.5] : [0, 2.5];
        for (const at of stabs) for (const pitch of voicing.map(x => x - 12 >= 50 ? x - 12 : x)) notes.push({ inst: 'keys', pitch, beat: t0 + at, beats: req.night ? 3.5 : 1, vel: .5 + .15 * e + hum.range(-.05, .05) });
        layers.add('keys');
      }

      // ── bass ──────────────────────────────────────────────────────────────
      const bassOn = ambient ? s.kind === 'chorus' : e >= .35 || (drive && s.kind === 'intro');
      if (bassOn) {
        const root = pedal ? pc(0) + 36 : pc(chord.root % 12) + 36, r = root > 45 ? root - 12 : root;
        const fifth = r + 7;
        if (ambient) { if (b % 2 === 0) notes.push({ inst: 'bass', pitch: r, beat: t0, beats: 8, vel: .7 }); }
        else if (drive) for (let i = 0; i < 16; i++) notes.push({ inst: 'bass', pitch: r + (i % 8 === 7 && e > .7 ? 12 : 0), beat: t0 + i / 4, beats: .2, vel: (i % 4 === 0 ? 1 : .7) * vel, bright });
        else if (chill) {
          const line: [number, number, number][] = e > .6 ? [[0, r, 1.4], [1.5, fifth, .45], [2, r + 12, .9], [3, fifth, .45], [3.5, r + 10, .45]] : [[0, r, 1.4], [1.5, fifth, .45], [2, r + 12, .9], [3.5, fifth - 12 >= 28 ? fifth - 12 : fifth, .45]];
          for (const [at, pitch, len] of line) notes.push({ inst: 'bass', pitch, beat: t0 + at, beats: len, vel: .85 * vel, bright });
        } else {
          if (e < .55) {
            // Held, melodic bass: the root for two beats, then a step toward the next chord.
            let nextRoot = pc(diatonic(prog[(Math.floor(b / harmonicBars) + 1) % prog.length], 'triad').root % 12) + 36;
            while (nextRoot - r > 6) nextRoot -= 12; while (r - nextRoot > 6) nextRoot += 12;
            // Chromatic approach: a semitone into the next root (or the fifth when it repeats).
            const approach = nextRoot === r ? r + 7 : nextRoot + (nextRoot > r ? -1 : 1);
            notes.push({ inst: 'bass', pitch: r, beat: t0, beats: 2, vel: .9 * vel, bright }, { inst: 'bass', pitch: r + 7 > 52 ? r - 5 : r + 7, beat: t0 + 2, beats: 1, vel: .8 * vel, bright }, { inst: 'bass', pitch: approach, beat: t0 + 3, beats: 1, vel: .75 * vel, bright });
          } else if (e < .82) {
            for (let q = 0; q < 4; q++) notes.push({ inst: 'bass', pitch: r, beat: t0 + q, beats: .5, vel: (q % 2 ? .82 : .98) * vel, bright }); // pumping quarters
          } else {
            const hits = euclid(11, 16, 0); // busier syncopation at the peak
            hits.forEach((on, i) => { if (on) notes.push({ inst: 'bass', pitch: r + (i % 4 === 2 ? 12 : 0), beat: t0 + i / 4, beats: .22, vel: (i % 4 === 0 ? .95 : .72) * vel, bright }); });
          }
        }
        layers.add('bass');
      }

      // ── arp: odd-length shape phasing against the bar ─────────────────────
      const arpOn = ambient ? s.kind === 'break' || s.kind === 'chorus' || s.kind === 'verse' : e >= .15 && s.kind !== 'break' || (s.kind === 'break' && b >= s.bars - 2);
      if (arpOn) {
        const octave = e > .8 ? 5 : 4, pool = [...chord.tones, ...chord.tones.map(x => x + 12), ...chord.tones.map(x => x + 24)].map(x => pc(x) + 12 * (octave + 1)).sort((u, v) => v - u).slice(1);
        const rate = ambient ? .5 : chill && e < .5 ? .5 : .25;
        for (let i = 0; i < 4 / rate; i++) {
          const step = Math.round((t0 + i * rate) / rate);
          if (ambient && hum.chance(.35)) continue;
          if (e < .3 && i % 2) continue;
          const run = Math.floor(step / arpLen);
          notes.push({ inst: 'arp', pitch: pool[(arpShape[step % arpLen] + (run % 2)) % pool.length], beat: t0 + i * rate, beats: rate * .7, vel: (i % 4 === 0 ? .9 : .62) * vel, pan: i % 2 ? .3 : -.3, bright });
        }
        layers.add('arp');
      }

      // ── lead: developed motif, question/answer phrases ─────────────────────
      const leadOn = ambient ? s.kind === 'chorus' || (s.kind === 'verse' && e > .3) : s.kind === 'verse' || s.kind === 'chorus' || (chill && s.kind === 'bridge');
      if (leadOn) {
        layers.add('lead');
        const memKey = `${s.kind}|${barPhrase}`;
        const reuse = leadMemory.get(memKey);
        const sectionCount = s.kind === 'chorus' ? chorusCount : verseCount;
        if (reuse && sectionCount > 1 && mr.chance(.7)) {
          // Repeat with variation: same phrase, optional octave doubling in the last chorus.
          for (const n of reuse) {
            const moved = { ...n, beat: t0 + (n.beat % 4), pitch: n.pitch + shift, vel: n.vel * (.95 + .1 * e) };
            notes.push(moved);
            if (isLastChorus) notes.push({ ...moved, pitch: moved.pitch + 12, vel: moved.vel * .45 });
          }
        } else {
          const motif = s.kind === 'chorus' ? chorusMotif : verseMotif;
          // Question (bars 0-1) ends open; answer (2-3) resolves home.
          const name = barPhrase === 0 ? 'repeat' : barPhrase === 1 ? mr.pick(['sequence', 'inversion', 'displace']) : barPhrase === 2 ? mr.pick(['repeat', 'retrograde', 'fragment']) : mr.pick(['sequence', 'fragment', 'repeat']);
          let phrase = TRANSFORMS[name](motif, mr);
          if (ambient) phrase = phrase.filter((_, i) => i % 2 === 0);
          const made: SNote[] = [];
          const register = s.kind === 'chorus' ? 5 : 4, chordDegrees = chord.tones.map(x => x);
          phrase.forEach((n, i) => {
            const last = i === phrase.length - 1;
            let pitch = scale.pitch(n.d + (s.kind === 'chorus' ? 2 : 0), register) + shift;
            // Strong beats and phrase endings land on chord tones.
            if (n.t % 1 === 0 || last) {
              const want = last && barPhrase === 3 ? [chord.tones[0]] : last && barPhrase === 1 ? chordDegrees.slice(1) : chordDegrees;
              let best = pitch;
              for (let o = -24; o <= 24; o += 12) for (const x of want) { const c = pc(x) + 12 * (register + 1) + o; if (Math.abs(c - pitch) < Math.abs(best - pitch) || best === pitch) best = c; }
              pitch = best;
            }
            const nextT = phrase[i + 1]?.t ?? (last && (barPhrase === 1 || barPhrase === 3) ? 5.5 : 4);
            const beats = Math.max(.25, Math.min(3, (nextT - n.t) * .9));
            // Portamento swoops between close notes; phrase endings often fall away.
            const fall = last && (barPhrase === 1 || barPhrase === 3) && beats >= .75 && mr.chance(.6) ? mr.pick([-12, -5, -7]) : undefined;
            const note: SNote = { inst: 'lead', pitch, beat: t0 + n.t, beats, vel: (.8 + hum.range(-.07, .05)) * vel, glide: prevLead > 0 && Math.abs(pitch - prevLead) <= 7 && mr.chance(.5), throw: last && barPhrase === 3, bright, bend: fall };
            prevLead = pitch; made.push(note); notes.push(note);
            if (isLastChorus) notes.push({ ...note, pitch: note.pitch + 12, vel: note.vel * .45, throw: false });
          });
          if (!leadMemory.has(memKey)) leadMemory.set(memKey, made.map(n => ({ ...n, pitch: n.pitch - shift })));
        }
      }

      // ── counter-melody: bells answer in the lead's gaps ───────────────────
      const counterOn = (s.kind === 'chorus' && e > .7) || s.kind === 'break' || (ambient && s.kind !== 'intro') || s.kind === 'outro';
      if (counterOn) {
        const busy = notes.filter(n => n.inst === 'lead' && n.beat >= t0 && n.beat < t0 + 4).map(n => [n.beat - t0, n.beat - t0 + n.beats]);
        const free = [0, .5, 1, 1.5, 2, 2.5, 3, 3.5].filter(x => !busy.some(([u, v]) => x >= u - .25 && x < v));
        const count = s.kind === 'break' || ambient ? counter.int(1, 3) : counter.int(1, 2);
        const tones = [...chord.tones].reverse();
        // Keep bells at or below C6: higher reads as shrill.
        const belowC6 = (x: number) => { while (x > 84) x -= 12; return x; };
        for (let k = 0; k < Math.min(count, free.length); k++) {
          const at = free[Math.floor(counter.next() * free.length)];
          notes.push({ inst: 'bell', pitch: belowC6(pc(tones[k % tones.length]) + 72 - (ambient ? 0 : 12)), beat: t0 + at, beats: 1.5, vel: .45 + .3 * e, pan: counter.range(-.6, .6), throw: k === 0 && phraseEnd });
        }
        layers.add('bell');
      }

      // ── drums: energy-driven, Euclidean, fills and drops ──────────────────
      const drumsOn = !ambient && e >= (chill ? .3 : .38) && s.kind !== 'break' && s.kind !== 'intro' && s.kind !== 'outro';
      const halftime = req.night;
      const dropBeat = intoChorus && drums.chance(.7); // silence the last beat before a chorus
      const keep = (beat: number) => !(dropBeat && beat >= 3);
      if (drumsOn) {
        const kicks = drive ? [0, 1, 2, 3] : chill ? [0, 1.75, 2.5] : halftime ? [0, 2.5] : e > .8 ? [0, 2, 2.75] : [0, 2];
        for (const k of kicks) if (keep(k)) notes.push({ inst: 'kick', pitch: 36, beat: t0 + k, beats: .5, vel: 1 });
        const back = halftime ? [2] : [1, 3];
        for (const k of back) if (keep(k)) notes.push({ inst: e > .75 && !chill ? 'clap' : 'snare', pitch: 38, beat: t0 + k, beats: .3, vel: (chill ? .6 : .9) * vel });
        if (e > .75 && !chill) for (const k of back) if (keep(k)) notes.push({ inst: 'snare', pitch: 38, beat: t0 + k + .005, beats: .3, vel: .5 });
        // Hats: density follows energy; Euclidean ghost pattern for movement.
        if (!(halftime && !drive)) {
          const hk = e > .8 ? 12 : e > .55 ? 8 : 5;
          euclid(hk, 16, drums.int(0, 3)).forEach((on, i) => { if (on && keep(i / 4)) notes.push({ inst: chill ? 'shaker' : 'hat', pitch: 42, beat: t0 + i / 4 + (chill && i % 2 ? .04 : 0), beats: .1, vel: ((i % 4 === 2 ? .8 : .45) * vel + drums.range(0, .1)) * .75 }); });
        }
        if (e > .7 && !halftime && keep(3.5)) notes.push({ inst: 'ohat', pitch: 46, beat: t0 + (drive ? 2.5 : 3.5), beats: .5, vel: .5 });
        // Percussion layer on a rotated Euclidean pattern.
        if (e > .5) euclid(percK, 8, percRot).forEach((on, i) => { if (on && keep(i / 2)) notes.push({ inst: 'shaker', pitch: 70, beat: t0 + i / 2 + .25, beats: .1, vel: .35 }); });
        // Fills at phrase ends: bigger into a chorus.
        if ((phraseEnd || sectionEnd) && !dropBeat && (sectionEnd || drums.chance(.45))) {
          const kind = intoChorus ? fillKind : drums.pick(['snare-roll', 'toms', 'stutter'] as const);
          const from = intoChorus ? 2 : 3;
          for (let x = from; x < 4; x += kind === 'stutter' ? .125 : .25) {
            const v = .45 + .5 * (x - from) / (4 - from);
            if (kind === 'toms') notes.push({ inst: 'tom', pitch: 52 - Math.floor((x - from) * 4) * 2, beat: t0 + x, beats: .3, vel: v });
            else notes.push({ inst: kind === 'stutter' ? 'hat' : 'snare', pitch: 38, beat: t0 + x, beats: .1, vel: v });
          }
          layers.add(fillKind === 'toms' ? 'tom' : 'snare');
        }
        layers.add('kick'); layers.add('snare'); layers.add(chill ? 'shaker' : 'hat');
      }
      // Stop-time: at some phrase ends INSIDE a section the rhythm section drops
      // out for half a beat. Held chords, lead tails and reverb carry through, so
      // it breathes without ever going silent (section boundaries use the drum drop).
      if (!ambient && phraseEnd && !sectionEnd && s.energy >= .4 && breakR.chance(pulseScene ? .45 : .25)) breaks.push({ beat: t0 + 3.5, beats: .5 });
      // Transitions: risers into big sections, crash on their downbeat.
      if (sectionEnd && next && (next.energy - s.energy > .2 || next.kind === 'chorus')) {
        notes.push({ inst: 'riser', pitch: 60, beat: t0 - 4 >= 0 ? t0 - 4 : t0, beats: t0 - 4 >= 0 ? 8 : 4, vel: .5 + .3 * next.energy });
        layers.add('riser');
      }
      if (b === 0 && s.kind === 'chorus' && !ambient) { notes.push({ inst: 'crash', pitch: 49, beat: t0, beats: 2, vel: .8 }); layers.add('crash'); }
    }
    // Master tone: breakdowns close down and reopen into the next section.
    const start = (bar - s.bars) * 4, end = bar * 4;
    const open = req.night ? 5200 : 16000;
    if (s.kind === 'break' || (ambient && s.kind === 'intro')) automation.push({ beat: start, tone: open * .18 }, { beat: end - 4, tone: open * .25 }, { beat: end, tone: open });
    else automation.push({ beat: start, tone: open });
    sections.push({ label: s.label, bar: bar - s.bars, bars: s.bars, layers: [...layers] });
  }
  const bars = bar, beats = bars * 4;
  const RHYTHM = new Set<InstId>(['kick', 'snare', 'clap', 'hat', 'ohat', 'shaker', 'tom', 'bass', 'stab', 'arp']);
  const inBreak = (n: SNote) => RHYTHM.has(n.inst) && breaks.some(b => n.beat >= b.beat - 1e-6 && n.beat < b.beat + b.beats);
  const clean = notes.filter(n => n.beat >= 0 && n.beat < beats && !inBreak(n)).sort((x, y) => x.beat - y.beat);
  const fxr = stream('fx');
  const fx: FxParams = {
    reverbSeconds: p.reverb * (req.night ? 1.25 : 1) * (ambient ? 1.3 : 1), reverbTone: fxr.range(3500, 7000) * p.bright,
    delayBeats: fxr.pick([.75, .75, .5, 1.5]), delayFeedback: p.delayFb, drive: p.drive,
    tone: req.night ? 5200 : 16000, duckDepth: ambient ? 0 : chill ? .25 : .5, width: .55,
    // Headroom: ~-14 dB RMS with real peaks, so the safety limiter rarely acts.
    master: .45,
  };
  // Bass-forward, low-glare mix (measured against the user's reference track).
  const mix: Partial<Record<InstId, number>> = { bell: -2, bass: 3, kick: 1, lead: 1.5, arp: -3, stab: -2, hat: -9, ohat: -9, shaker: -6, snare: -5, clap: -5, crash: -7, riser: -4 };
  const modeName = mode[0].toUpperCase() + mode.slice(1);
  const chordLabel = (prog: number[]) => prog.map(d => chordName(diatonic(d, 'triad')) + (scale.quality(d) === 'min' ? 'm' : '')).join('–');
  return {
    version: SYNTH_COMPOSER_VERSION_2, composer: 2, request: req, style: a.style + (req.night ? ' (night)' : ''), title,
    description: `${a.blurb} ${p.tonal[0].toUpperCase() + p.tonal.slice(1)} ${req.biome} palette in ${KEYS[key]} ${modeName}. Varied: energy arc, ${pulseScene ? 'chopped pulse chords, gliding lead, stop-time breaks, ' : ''}developed ${colour === 'triad' ? '' : colour + ' '}harmony, ${arpLen}-step phasing arp, Euclidean percussion${lift ? ', final chorus lifts a whole step' : ''}.${req.night ? ' Night: slower, darker, thinner drums.' : ''}`,
    bpm, key, keyName: KEYS[key], mode: modeName, swing: chill ? .1 : 0, bars, beats, duration: beats * 60 / bpm,
    progression: [`verse ${chordLabel(verseProg)}`, `chorus ${chordLabel(chorusProg)}${useBorrowed ? ` + borrowed ${chordName(borrowed)}` : ''}`, ...(lift ? ['last chorus +2 semitones'] : [])],
    sections, patches, notes: clean, fx, energy, automation, breaks, mix,
  };
}
